import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { PriceComponent } from '@df/ui/price/price.component';
import { UiComponent } from '@df/ui/ui.component';
import { IPlaceholderData, ITextFragment } from './cms-text-processor.interfaces';

/**
 * Simple component for rendering CMS text with price components
 */
@Component({
    selector: 'cms-text-renderer',
    template: `
        @for (fragment of fragments; track $index) {
            @if (fragment.type === 'text') {
                <span>{{ fragment.content }}</span>
            } @else if (fragment.type === 'price') {
                <price [value]="fragment.value || 0"></price>
            } @else if (fragment.type === 'percentage') {
                <span>{{ fragment.content }}</span>
            }
        }
    `,
    standalone: true,
    imports: [CommonModule, PriceComponent],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class CmsTextRendererComponent extends UiComponent {
    @Input()
    text: string = '';

    @Input()
    data: IPlaceholderData = {};

    protected get fragments(): ITextFragment[] {
        if (!this.text) return [];

        const fragments: ITextFragment[] = [];
        let remainingText = this.text;

        // Process each placeholder type
        const placeholders = [
            { pattern: /\[product price\]/gi, key: 'productPrice', type: 'price' as const },
            { pattern: /\[deposit value\]/gi, key: 'depositValue', type: 'price' as const },
            { pattern: /\[loan amount\]/gi, key: 'loanAmount', type: 'price' as const },
            { pattern: /\[monthly payment\]/gi, key: 'monthlyPayment', type: 'price' as const },
            { pattern: /\[total amount\]/gi, key: 'totalAmount', type: 'price' as const },
            { pattern: /\[deposit percentage\]/gi, key: 'depositPercentage', type: 'percentage' as const },
            { pattern: /\[interest rate\]/gi, key: 'interestRate', type: 'percentage' as const },
            { pattern: /\[terms\]/gi, key: 'terms', type: 'text' as const }
        ];

        // Find all matches and their positions
        const matches: Array<{
            index: number;
            length: number;
            type: 'price' | 'percentage' | 'text';
            value?: number;
            content: string;
        }> = [];

        placeholders.forEach(placeholder => {
            let match;
            const regex = new RegExp(placeholder.pattern.source, placeholder.pattern.flags);
            while ((match = regex.exec(remainingText)) !== null) {
                const value = this.data[placeholder.key as keyof IPlaceholderData];
                matches.push({
                    index: match.index,
                    length: match[0].length,
                    type: placeholder.type,
                    value: typeof value === 'number' ? value : undefined,
                    content: placeholder.type === 'percentage' ? `${value || 0}%` :
                            placeholder.type === 'text' ? (value?.toString() || '0') : ''
                });
            }
        });

        // Sort matches by position
        matches.sort((a, b) => a.index - b.index);

        // Build fragments
        let lastIndex = 0;
        matches.forEach(match => {
            // Add text before match
            if (match.index > lastIndex) {
                fragments.push({
                    type: 'text',
                    content: remainingText.substring(lastIndex, match.index)
                });
            }

            // Add the match
            fragments.push({
                type: match.type,
                content: match.content,
                value: match.value
            });

            lastIndex = match.index + match.length;
        });

        // Add remaining text
        if (lastIndex < remainingText.length) {
            fragments.push({
                type: 'text',
                content: remainingText.substring(lastIndex)
            });
        }

        // If no matches, return the whole text
        if (matches.length === 0) {
            fragments.push({
                type: 'text',
                content: remainingText
            });
        }

        return fragments;
    }
}
